import { Grid, Button, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useState, useMemo, useContext } from 'react';
import { CONSTANTS } from 'src/components/Constants';
import { SnackContext, AppContext } from 'src/store/ContextStore';
import HistoryIcon from '@mui/icons-material/History';
import CanRiskModal from './export-menu/CanRiskModal';
import { downloadCanRisk } from './export-menu/canriskDownload';
import RiskRecordsList from './RiskRecordsList';
import GailScoreModal from './GailScoreModal';

const ButtonGroup = styled(Grid)({
  display: 'flex',
  justifyContent: 'center',
  marginTop: 15
});

function RiskFactorsMenu({ data, modificationHistoryHandle }) {
  const { setSnack } = useContext(SnackContext);
  const [recordsRefreshKey, setRecordsRefreshKey] = useState(0);
  const [gailModalOpen, setGailModalOpen] = useState(false);
  const [canRiskModalOpen, setCanRiskModalOpen] = useState(false);

  const { content } = useContext(AppContext);
  const { pedigreeSelectedUsers: users } = content;
  const { selectedUser: user } = users;

  const user_id = useMemo(() => user?.user_id || user?._id, [user]);
  const familyId = user?.familyId || user_id;
  const gender = useMemo(() => data?.gender, [data]);
  // Canonical member key — scopes the score cards to the selected member.
  const memberId = useMemo(
    () => data?.proBandId ?? data?.familyMemberId,
    [data?.proBandId, data?.familyMemberId]
  );

  const handleCanRiskDownload = (allOptionsArray, targetId) =>
    downloadCanRisk({
      userId: user_id,
      familyId,
      members: allOptionsArray,
      targetId,
      setSnack
    });

  return (
    <div style={{ maxWidth: 390 }}>
      <Grid container spacing={2}>
        {gender === 'female' && (
          <ButtonGroup size={12}>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => setGailModalOpen(true)}
              data-va-anchor="pedigree.risk.calculateGail"
              sx={{ width: '100%' }}
            >
              Calculate Gail Score
            </Button>
          </ButtonGroup>
        )}

        <ButtonGroup size={12}>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => setCanRiskModalOpen(true)}
            data-va-anchor="pedigree.risk.calculateCanRisk"
            sx={{ width: '100%' }}
          >
            Calculate CanRisk Score
          </Button>
        </ButtonGroup>

        {gender === 'female' && (
          <Grid size={12}>
            <RiskRecordsList
              userId={user_id}
              refreshKey={recordsRefreshKey}
              recordType="gail"
              memberIds={[memberId]}
              scoredOnly
            />
          </Grid>
        )}
        <Grid size={12}>
          <RiskRecordsList
            userId={user_id}
            refreshKey={recordsRefreshKey}
            recordType="canrisk"
            memberIds={[memberId]}
            scoredOnly
          />
        </Grid>
        {/* PRS is stored, not calculated in-app (no genotype/VCF data) — a
            score is entered via the "Risk Factor" clinical record (PRS radio
            option) and simply surfaces here as a peer to Gail/CanRisk. */}
        <Grid size={12}>
          <RiskRecordsList
            userId={user_id}
            refreshKey={recordsRefreshKey}
            recordType="prs"
            memberIds={[memberId]}
            scoredOnly
          />
        </Grid>
        {/* Not sex-gated: CanRisk covers prostate and pancreatic cancer, and PRS
            is trait-agnostic, so male individuals have risk records too — gating
            this on female left their audit history unreachable. The Gail list
            above stays female-gated because the Gail model itself is. */}
        <Grid
          size={12}
          sx={{
            display: 'flex',
            justifyContent: 'flex-end'
          }}
        >
          <IconButton
            onClick={() =>
              modificationHistoryHandle(CONSTANTS.auditLogType['riskFactors'])
            }
          >
            <HistoryIcon />
          </IconButton>
        </Grid>
      </Grid>
      {/* Calculate Gail Score - fetches saved inputs on open (no form) */}
      <GailScoreModal
        open={gailModalOpen}
        onClose={() => setGailModalOpen(false)}
        data={data}
        familyId={familyId}
        onCalculated={() => setRecordsRefreshKey((k) => k + 1)}
      />
      <CanRiskModal
        data={data}
        open={canRiskModalOpen}
        onClose={(hadResult) => {
          setCanRiskModalOpen(false);
          if (hadResult) setRecordsRefreshKey((k) => k + 1);
        }}
        onDownload={handleCanRiskDownload}
        userId={user_id}
      />
    </div>
  );
}

export default RiskFactorsMenu;
